import type {
  Baseline,
  IElementID,
  Project,
} from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';
import { getAllBuildingVersions } from '../recursive-element.helpers';
import { partitionProjectChangesForProposalContext } from '../actions/apply-project-changes-with-proposal-context.helpers';
import {
  buildElementPathIndex,
  type RelativePathIndexMap,
} from './batch-update.helpers';
import { getRecipeScopedParentElementId } from '../recipe.helpers';
import { guards } from '../typeguards.helpers';
import { ProjectChangeType, type ProjectChange } from './update-engine.models';

/**
 * Per-version description of which parts of the enrich pipeline must run.
 * `EnrichScope` is the per-project aggregation; absent versions are skipped entirely.
 */
export type VersionEnrichScope = {
  /**
   * Run the baseline compute steps (collect compute nodes, resolve expressions,
   * write back values to the project). Disabled when only proposal-scoped or
   * pure-metadata changes were applied.
   */
  needsBaselineCompute: boolean;
  /**
   * Whether structural enrichment (autoQuantity, category & data table sync)
   * must walk every element of the version. When `false`, only the elements
   * listed in {@link structuralElementIds} (and their subtrees) are touched.
   */
  structuralAll: boolean;
  /**
   * Element ids requiring structural enrichment together with their subtrees.
   * Ignored when {@link structuralAll} is `true`.
   */
  structuralElementIds: Set<IElementID>;
  /**
   * Refresh {@link Baseline.products} for the version (step 4 of the
   * pipeline). Triggered by anything that changes which products are referenced.
   */
  needsProductRecordUpdate: boolean;
  /**
   * Which proposals must be recomputed in step 5b. `'all'` means every proposal
   * on the version (baseline-affecting changes); a `Set` lists the explicit
   * proposal ids that need a fresh result record.
   */
  proposalsToRecompute: Set<ProposalId> | 'all';
};

/**
 * Per-project enrichment plan derived from a {@link ProjectChange} batch.
 * Versions not present in {@link versions} can be skipped completely; the
 * pipeline does not need to inspect or rebuild their result records.
 */
export type EnrichScope = {
  versions: Map<IElementID, VersionEnrichScope>;
};

type BuildEnrichScopeOptions = {
  /**
   * The proposal that was selected when the changes were applied. When set,
   * element-level edits that are not baseline-only land on the proposal’s
   * action list (see {@link partitionProjectChangesForProposalContext}).
   */
  selectedProposalId?: ProposalId;
};

/**
 * Element-level change types that change the structure of the tree or the
 * shape of an element (so autoQuantity / category sync must re-run on the
 * element and its subtree).
 *
 * Quantity count add/remove transitions ({@link ProjectChangeType.ElementQuantityCount},
 * {@link ProjectChangeType.ElementQuantityFallbackCount}, {@link ProjectChangeType.ElementQuantitySelect})
 * are included because they flip whether autoQuantity emits a value.
 */
const STRUCTURAL_ELEMENT_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementAdd,
  ProjectChangeType.ElementRemove,
  ProjectChangeType.ElementCategory,
  ProjectChangeType.ElementCategoryEnrich,
  ProjectChangeType.ElementRecipeApply,
  ProjectChangeType.ElementRecipeDetach,
  ProjectChangeType.ElementRecipeClear,
  ProjectChangeType.ElementPropertyAdd,
  ProjectChangeType.ElementPropertyRemove,
  ProjectChangeType.ElementDataTablePropertiesApply,
  ProjectChangeType.ElementQuantityAutoUpdate,
  ProjectChangeType.ElementQuantityCount,
  ProjectChangeType.ElementQuantityFallbackCount,
  ProjectChangeType.ElementQuantitySelect,
  ProjectChangeType.ElementSoftDelete,
  ProjectChangeType.ElementActivate,
  ProjectChangeType.ElementDeactivate,
]);

/**
 * Element-level change types that modify expression / value / formatting state
 * but NOT the tree shape. These need a baseline recompute, but skip autoQuantity.
 */
/** Quantity value edits can change which auto-fallbacks apply (DEV-3332). */
const QUANTITY_VALUE_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementQuantityResolved,
  ProjectChangeType.ElementQuantityExpression,
  ProjectChangeType.ElementQuantityFallbackExpression,
  ProjectChangeType.ElementQuantityFormatted,
  ProjectChangeType.ElementQuantityValue,
]);

const VALUE_ELEMENT_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementPropertyResolved,
  ProjectChangeType.ElementPropertyExpression,
  ProjectChangeType.ElementPropertyFormatted,
  ProjectChangeType.ElementPropertyValue,
  ProjectChangeType.ElementPropertyFallbackCount,
  ProjectChangeType.ElementPropertyCount,
  ProjectChangeType.ElementPropertyRename,
  ProjectChangeType.ElementQuantityResolved,
  ProjectChangeType.ElementQuantityExpression,
  ProjectChangeType.ElementQuantityFallbackExpression,
  ProjectChangeType.ElementQuantityFormatted,
  ProjectChangeType.ElementQuantityValue,
  ProjectChangeType.ProductElementCountResolved,
  ProjectChangeType.ProductElementCountExpression,
  ProjectChangeType.ProductElementCountFormatted,
  ProjectChangeType.ProductElementProductId,
  ProjectChangeType.ProductElementEpd,
  ProjectChangeType.ProductElementTransportOverride,
  ProjectChangeType.ProductElementWasteOverride,
  ProjectChangeType.ProductElementCarbonLevelOverride,
]);

/**
 * Element-level change types that are pure metadata: do not affect compute
 * output and therefore do not require any pipeline work.
 */
const PURE_METADATA_ELEMENT_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementName,
]);

/**
 * Version-level change types that are pure metadata writes / pipeline
 * outputs — applying them must not trigger any recompute.
 */
const PURE_METADATA_VERSION_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.VersionName,
  ProjectChangeType.VersionResults,
  ProjectChangeType.VersionProducts,
  ProjectChangeType.ProposalResultsRecord,
  ProjectChangeType.ProposalReorder,
  ProjectChangeType.ActionRename,
]);

/**
 * Element-level change types that imply a refresh of the version’s product
 * record (because the set of referenced products may have changed).
 */
const PRODUCT_RECORD_REFRESH_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementAdd,
  ProjectChangeType.ElementRemove,
  ProjectChangeType.ElementRecipeApply,
  ProjectChangeType.ElementRecipeDetach,
  ProjectChangeType.ElementRecipeClear,
  ProjectChangeType.ElementCategory,
  ProjectChangeType.ElementCategoryEnrich,
  ProjectChangeType.ProductElementProductId,
  ProjectChangeType.ProductElementEpd,
  ProjectChangeType.ProductElementTransportOverride,
  ProjectChangeType.ProductElementWasteOverride,
  ProjectChangeType.ProductElementCarbonLevelOverride,
]);

/**
 * Mutable working scope used while iterating changes. Frozen into a
 * {@link VersionEnrichScope} when the build completes.
 */
type ScopeBuilder = {
  needsBaselineCompute: boolean;
  structuralAll: boolean;
  structuralElementIds: Set<IElementID>;
  needsProductRecordUpdate: boolean;
  proposalIds: Set<ProposalId>;
  recomputeAllProposals: boolean;
};

const createBuilder = (): ScopeBuilder => ({
  needsBaselineCompute: false,
  structuralAll: false,
  structuralElementIds: new Set(),
  needsProductRecordUpdate: false,
  proposalIds: new Set(),
  recomputeAllProposals: false,
});

const finalize = (b: ScopeBuilder): VersionEnrichScope => ({
  needsBaselineCompute: b.needsBaselineCompute,
  structuralAll: b.structuralAll,
  // Drop the explicit set when we already cover the whole version
  structuralElementIds: b.structuralAll ? new Set() : b.structuralElementIds,
  needsProductRecordUpdate: b.needsProductRecordUpdate,
  proposalsToRecompute: b.recomputeAllProposals ? 'all' : b.proposalIds,
});

/**
 * Mark the version as fully invalidated. Used as the fail-safe when an
 * unknown change type is encountered or when we cannot resolve the targeted
 * version cheaply.
 */
const markFullScope = (b: ScopeBuilder): void => {
  b.needsBaselineCompute = true;
  b.structuralAll = true;
  b.needsProductRecordUpdate = true;
  b.recomputeAllProposals = true;
};

const ensureBuilder = (
  scopes: Map<IElementID, ScopeBuilder>,
  versionId: IElementID,
): ScopeBuilder => {
  let builder = scopes.get(versionId);
  if (!builder) {
    builder = createBuilder();
    scopes.set(versionId, builder);
  }
  return builder;
};

/**
 * Build a `Map<elementId, versionId>` for fast change-to-version routing.
 * Uses the cached {@link buildElementPathIndex} per version.
 */
const buildElementToVersionIndex = (
  project: Project,
): {
  elementToVersion: Map<IElementID, IElementID>;
  versions: Baseline[];
} => {
  const elementToVersion = new Map<IElementID, IElementID>();
  const versions = getAllBuildingVersions(project);
  for (const version of versions) {
    const index: RelativePathIndexMap = buildElementPathIndex(version);
    for (const id of index.keys()) {
      elementToVersion.set(id, version.id);
    }
  }
  return { elementToVersion, versions };
};

/**
 * Apply one baseline (i.e. project-tree) change to a {@link ScopeBuilder}.
 * Baseline changes always trigger every proposal to recompute because
 * each proposal materialises as `baseline + actions`.
 */
const applyBaselineChange = (b: ScopeBuilder, change: ProjectChange): void => {
  // Pure metadata edits do not touch compute output
  if (PURE_METADATA_ELEMENT_CHANGE_TYPES.has(change.type)) return;
  if (PURE_METADATA_VERSION_CHANGE_TYPES.has(change.type)) return;

  if (PRODUCT_RECORD_REFRESH_TYPES.has(change.type)) {
    b.needsProductRecordUpdate = true;
  }

  if (VALUE_ELEMENT_CHANGE_TYPES.has(change.type)) {
    b.needsBaselineCompute = true;
    b.recomputeAllProposals = true;
    if (
      QUANTITY_VALUE_CHANGE_TYPES.has(change.type) &&
      'elementId' in change &&
      guards.is.string(change.elementId)
    ) {
      b.structuralElementIds.add(change.elementId);
    }
    return;
  }

  if (STRUCTURAL_ELEMENT_CHANGE_TYPES.has(change.type)) {
    b.needsBaselineCompute = true;
    b.recomputeAllProposals = true;
    // ElementRemove targets a removed id whose parent is unknown to us — bail to full structural enrich
    if (change.type === ProjectChangeType.ElementRemove) {
      b.structuralAll = true;
      return;
    }
    // Every structural change above has an `elementId` field
    if ('elementId' in change && guards.is.string(change.elementId)) {
      b.structuralElementIds.add(change.elementId);
    } else {
      b.structuralAll = true;
    }
    return;
  }

  // Version-scoped baseline changes — proposal lifecycle / actions
  switch (change.type) {
    case ProjectChangeType.VersionAdd:
      // Default: a brand-new tree has no reusable results, so this version is a
      // full run. Callers that clone + remap an existing {@link ResultsRecord}
      // (baseline-from-proposal) skip the worker instead of relying on this scope.
      // The builder is keyed on the new version id, so other versions stay untouched.
      markFullScope(b);
      return;
    case ProjectChangeType.VersionRemove:
      // The version and all its results are gone; other versions are independent.
      return;
    case ProjectChangeType.ProposalAdd:
      // The new proposal needs a fresh row, but baseline is unchanged
      b.recomputeAllProposals = true;
      return;
    case ProjectChangeType.ProposalDuplicate:
      // Cloned results are copied in the mutator; recompute is the fallback when
      // the apply layer does not skip enrichment (empty source resultsRecord).
      b.recomputeAllProposals = true;
      return;
    case ProjectChangeType.ProposalDelete:
      // Removed proposal — nothing else to recompute
      return;
    case ProjectChangeType.VersionClearProposals:
      // All actions disappeared but baseline tree is unchanged
      return;
    case ProjectChangeType.ActionDelete:
      // Cheap to over-approximate: any proposal that referenced the action must recompute
      b.recomputeAllProposals = true;
      return;
    case ProjectChangeType.ProposalAppendAction:
      // Direct ProposalAppendAction (not produced by partitioning) only invalidates that proposal
      b.proposalIds.add(change.proposalId);
      return;
    case ProjectChangeType.ProposalUpdate: {
      // Only `actionIds` updates affect compute; pure name/color/active edits are metadata
      const touchesActions = change.updates.some((u) => 'actionIds' in u);
      if (!touchesActions) return;
      for (const update of change.updates) {
        if ('actionIds' in update) b.proposalIds.add(update.id);
      }
      return;
    }
    default:
      // Unknown change type — fail-safe to a full pipeline run for this version
      markFullScope(b);
  }
};

/**
 * Apply one proposal-scoped change to the builder. Proposal changes never
 * recompute baseline; they only invalidate the targeted proposal’s result
 * record (the inner element edits are stored as an {@link Action}).
 */
const applyProposalChange = (
  b: ScopeBuilder,
  change: ProjectChange,
  selectedProposalId: ProposalId,
): void => {
  if (PURE_METADATA_ELEMENT_CHANGE_TYPES.has(change.type)) return;

  // Inner element / product element edits reduce to "this proposal must recompute"
  if (
    VALUE_ELEMENT_CHANGE_TYPES.has(change.type) ||
    STRUCTURAL_ELEMENT_CHANGE_TYPES.has(change.type)
  ) {
    b.proposalIds.add(selectedProposalId);
    return;
  }

  switch (change.type) {
    case ProjectChangeType.ProposalAppendAction:
      b.proposalIds.add(change.proposalId);
      return;
    default:
      // Unknown / unmodelled proposal-scoped change — invalidate the selected proposal
      b.proposalIds.add(selectedProposalId);
  }
};

const resolveVersionId = (
  change: ProjectChange,
  elementToVersion: Map<IElementID, IElementID>,
): IElementID | undefined => {
  if ('versionId' in change && guards.is.string(change.versionId)) {
    return change.versionId;
  }
  if ('elementId' in change && guards.is.string(change.elementId)) {
    const versionId = elementToVersion.get(change.elementId);
    if (versionId) return versionId;
    // Recipe-applied children are not in the baseline tree; route via parent id (DEV-3317).
    const parentId = getRecipeScopedParentElementId(change.elementId);
    if (parentId) return elementToVersion.get(parentId);
    return undefined;
  }
  return undefined;
};

/**
 * Compute an {@link EnrichScope} from a list of {@link ProjectChange} that
 * have been (or are about to be) applied to a project.
 *
 * The scope describes — per affected version — which pipeline steps must
 * run, which element subtrees need structural enrichment, and which
 * proposals need fresh result records. Versions absent from the scope can
 * be skipped completely.
 *
 * Behaviour notes:
 *  - Baseline changes (no selected proposal, or types excluded from proposal
 *    scoping) recompute baseline and mark every proposal for recompute.
 *  - Proposal-scoped changes (with `selectedProposalId`) only mark the
 *    targeted proposal — baseline stays unchanged.
 *  - Pure-metadata changes (rename, color, results writeback, …) do not
 *    contribute to the scope at all.
 *  - Unknown change types fall back to a full version invalidation so the
 *    scope is always safe to consume.
 *
 * @param project - Project the changes apply to. Used to resolve `elementId`
 *   → containing version id via {@link buildElementPathIndex}.
 * @param changes - Raw user-supplied changes; partitioning into baseline vs
 *   proposal happens internally via
 *   {@link partitionProjectChangesForProposalContext}.
 * @param options - {@link BuildEnrichScopeOptions}
 */
export const buildEnrichScopeFromChanges = (
  project: Project,
  changes: ProjectChange[],
  { selectedProposalId }: BuildEnrichScopeOptions = {},
): EnrichScope => {
  const scopes = new Map<IElementID, ScopeBuilder>();
  if (changes.length === 0) return { versions: new Map() };

  const { elementToVersion } = buildElementToVersionIndex(project);

  // Use the same partition logic as the actual apply path so scope inference
  // matches what the pipeline will actually do.
  const { baselineChanges, proposalChanges } =
    partitionProjectChangesForProposalContext(changes, selectedProposalId);

  for (const change of baselineChanges) {
    const versionId = resolveVersionId(change, elementToVersion);
    if (!versionId) {
      // Cannot route the change to a specific version → invalidate everything
      for (const version of getAllBuildingVersions(project)) {
        markFullScope(ensureBuilder(scopes, version.id));
      }
      continue;
    }
    applyBaselineChange(ensureBuilder(scopes, versionId), change);
  }

  if (selectedProposalId) {
    for (const change of proposalChanges) {
      const versionId = resolveVersionId(change, elementToVersion);
      if (!versionId) continue;
      applyProposalChange(
        ensureBuilder(scopes, versionId),
        change,
        selectedProposalId,
      );
    }
  }

  const versions = new Map<IElementID, VersionEnrichScope>();
  for (const [versionId, builder] of scopes) {
    versions.set(versionId, finalize(builder));
  }
  return { versions };
};

/**
 * True when nothing in the scope requires any pipeline work for the version
 * (no baseline compute, no structural enrichment, no proposal recompute,
 * no product record refresh). Call sites can short-circuit on this.
 */
export const isNoopVersionScope = (scope: VersionEnrichScope): boolean =>
  !scope.needsBaselineCompute &&
  !scope.structuralAll &&
  scope.structuralElementIds.size === 0 &&
  !scope.needsProductRecordUpdate &&
  scope.proposalsToRecompute !== 'all' &&
  scope.proposalsToRecompute.size === 0;
